import { useEffect, useRef } from "react";
import { useParams, useRevalidator, useRouteLoaderData } from "react-router";
import { FieldsData, ResultSet } from "../../../../../../API/fields";
import { listCalibrationMeasurementSets } from "../../../../../../API/calibration";
import {
  isStalled,
  useTrackedCalibrations,
} from "../../../../../../Component/CalibrationBanners/tracked-calibrations";
import {
  latestMeasurementSet,
  sourceResultSetIdOf,
} from "../../../../../../Component/CalibrationBanners/calibration-lineage";

const MAX_CONCURRENT_REQUESTS = 4;
const MAX_REVALIDATIONS = 5;

export function useCalibrationDiscovery(appleResultSets: ResultSet[]) {
  const { customerId, fieldId } = useParams();
  const customer = useRouteLoaderData("customer") as FieldsData | undefined;
  const fieldName =
    customer?.fields?.features.find(
      (feature) => feature.properties.id === Number(fieldId),
    )?.properties.name ?? "";

  const resultSetIdBySource = useRef(new Map<string, string>());
  resultSetIdBySource.current = new Map(
    appleResultSets.map((rs) => [sourceResultSetIdOf(rs), rs.id]),
  );
  const sourcesKey = JSON.stringify([
    ...new Set(appleResultSets.map(sourceResultSetIdOf)),
  ]);

  useEffect(() => {
    if (!customerId || !fieldId) return;
    let cancelled = false;
    const queue: string[] = JSON.parse(sourcesKey);
    const { track } = useTrackedCalibrations.getState();

    const worker = async () => {
      for (let source = queue.shift(); source; source = queue.shift()) {
        try {
          const latest = latestMeasurementSet(
            await listCalibrationMeasurementSets(source),
          );
          if (cancelled) return;
          if (latest?.status !== "in_progress") continue;
          if (isStalled(latest.created_at)) continue;
          track({
            id: latest.id,
            version: latest.version,
            createdAt: latest.created_at,
            sourceResultSetId: source,
            resultSetId: resultSetIdBySource.current.get(source) ?? source,
            customerId,
            fieldId,
            fieldName,
          });
        } catch (error) {
          console.warn("Failed to check for a running calibration", error);
        }
      }
    };
    void Promise.all(Array.from({ length: MAX_CONCURRENT_REQUESTS }, worker));

    return () => {
      cancelled = true;
    };
  }, [sourcesKey, customerId, fieldId, fieldName]);
}

export function useRevalidateOnCalibrationSuccess(resultSets: ResultSet[]) {
  const { revalidate } = useRevalidator();
  const attempts = useRef(new Map<string, number>());

  const awaitedId = useTrackedCalibrations(
    ({ calibrations }) =>
      calibrations.find(
        (tracked) =>
          tracked.status === "succeeded" &&
          resultSets.some(
            (rs) => sourceResultSetIdOf(rs) === tracked.sourceResultSetId,
          ) &&
          !resultSets.some(
            (rs) =>
              rs.calibration?.calibration_measurement_set_id === tracked.id,
          ),
      )?.id,
  );

  useEffect(() => {
    if (!awaitedId) return;
    const attempt = attempts.current.get(awaitedId) ?? 0;
    if (attempt >= MAX_REVALIDATIONS) return;

    const timer = setTimeout(
      () => {
        attempts.current.set(awaitedId, attempt + 1);
        void revalidate();
      },
      attempt === 0 ? 0 : 1000 * 2 ** attempt,
    );
    return () => clearTimeout(timer);
  }, [awaitedId, resultSets, revalidate]);
}

export function useLatestCalibratedSelection(
  selectedResultSet: ResultSet | undefined,
  displayedResultSets: ResultSet[],
  onSelect: (resultSetId: string, options: { replace: boolean }) => void,
) {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const isDisplayed = displayedResultSets.some(
    (rs) => rs.id === selectedResultSet?.id,
  );
  const replacementId =
    selectedResultSet && !isDisplayed
      ? displayedResultSets.find(
          (rs) =>
            sourceResultSetIdOf(rs) === sourceResultSetIdOf(selectedResultSet),
        )?.id
      : undefined;

  useEffect(() => {
    if (replacementId) onSelectRef.current(replacementId, { replace: true });
  }, [replacementId]);
}
